import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import SimpleSteps from "@/components/navigation/Steps";
import GeneralDescription from "./general_description";
import LocationProperty from "./location";
import Amenities from "./amenities";
import UploadMedia from "./upload_media";
import {
  useMyPropertiesCount,
  useProperty,
} from "@/_actions/propertiesActions";

function handleReload(e) {
  e.preventDefault();
  e.returnValue =
    "La información de la propiedad se perderá si sales ahora, ¿deseas abandonar la página?";
}

export default function Edit() {
  const { propertyId } = useParams();
  const location = useLocation();
  const [step, setStep] = useState(location.state?.step || 1);
  const singleSection = location.state?.singleSection || false;
  const navigate = useNavigate();

  const {
    data: allPropertiesCount,
    isLoading: isCountLoading,
    error: countError,
  } = useMyPropertiesCount();

  const {
    data: property,
    isLoading: isPropertyLoading,
    error: propertyError,
  } = useProperty(propertyId);

  const canPublish = Boolean(allPropertiesCount?.can_publish);
  const isPublished = property?.status === "published";
  const canEdit = canPublish || isPublished;

  useEffect(() => {
    window.addEventListener("beforeunload", handleReload);
    return () => {
      window.removeEventListener("beforeunload", handleReload);
    };
  }, []);

  useEffect(() => {
    if (isCountLoading || isPropertyLoading) return;
    if (countError || propertyError) return;
    if (!allPropertiesCount || !property) return;
    if (!canEdit) {
      navigate("/subscription", { replace: true });
    }
  }, [
    isCountLoading,
    isPropertyLoading,
    allPropertiesCount,
    property,
    canEdit,
    navigate,
    propertyId,
    countError,
    propertyError,
  ]);

  const gotoAmenitiesStep = () => {
    if (singleSection) finish();
    else setStep(2);
  };

  const gotoLocationStep = () => {
    if (singleSection) finish();
    else setStep(3);
  };

  const gotoMediaStep = () => {
    if (singleSection) finish();
    else setStep(4);
  };

  const finish = () => {
    navigate("/properties");
  };

  function setStatus(index, currentIndex) {
    if (index <= currentIndex) return "current";
    return "upcoming";
  }

  const propertyCreationSteps = useMemo(() => {
    return [
      {
        name: "Información General",
        description: "Inmobiliaria o agente independiente",
        href: "#",
        status: setStatus(1, step),
        onClick: (e) => {
          e.preventDefault();
          setStep(1);
        },
      },
      {
        name: "Amenidades",
        description: "Selecciona las amenidades de la propiedad",
        href: "#",
        status: setStatus(2, step),
        onClick: (e) => {
          e.preventDefault();
          setStep(2);
        },
      },
      {
        name: "Ubicación",
        description: "Define para preparar tu plan",
        href: "#",
        status: setStatus(3, step),
        onClick: (e) => {
          e.preventDefault();
          setStep(3);
        },
      },
      {
        name: "Fotografías",
        description: "Contrata tu plan y accede a PRO",
        href: "#",
        status: setStatus(4, step),
        onClick: (e) => {
          e.preventDefault();
          setStep(4);
        },
      },
    ];
  }, [step]);

  const goBack = () => setStep((prev) => prev - 1);

  if (isCountLoading || isPropertyLoading) return null;
  if (countError || propertyError) return null;
  if (!allPropertiesCount || !property) return null;
  if (!canEdit) return null;

  return (
    <div className="rounded bg-white">
      <SimpleSteps steps={propertyCreationSteps} />

      {(step === 1 || step === "1") && (
        <GeneralDescription
          callBack={gotoAmenitiesStep}
          propertyId={propertyId}
          action="edit"
        />
      )}

      {(step === 2 || step === "2") && (
        <Amenities
          callBack={gotoLocationStep}
          propertyId={propertyId}
          goBack={goBack}
          action="edit"
        />
      )}

      {(step === 3 || step === "3") && (
        <LocationProperty
          callBack={gotoMediaStep}
          propertyId={propertyId}
          goBack={goBack}
          action="edit"
        />
      )}

      {(step === 4 || step === "4") && (
        <UploadMedia
          callBack={finish}
          propertyId={propertyId}
          goBack={goBack}
          action="edit"
        />
      )}
    </div>
  );
}
